import {
  AltiusRes,
  BenchmarkCalculatedField,
  BenchmarkDealWithResult,
  BenchmarkForecastGraph,
  BenchmarkForecastGraphName,
  BenchmarkLiquidityGraph,
  BenchmarkLiquidityGraphParams,
  BenchmarkQueries as BenchmarkQueriesType,
  DateRangeValue,
  DealGraph,
  GetReportsDataResponse,
  ReportFilters,
  ReportPageType,
  ReportsQueries
} from "../../../store/types";
import { AsyncQueries, AsyncQueriesParams } from "../base";
import { convertDateRange } from "../../../utils";

export interface IBenchmarkQueries {
  getBenchmarkFilters: (filters: ReportFilters) => Promise<ReportFilters>;
  getBenchmarkGraph: (queries: BenchmarkQueriesType) => Promise<DealGraph>;
  getBenchmarkForecastGraph: (
    queries: Pick<ReportsQueries, "filters" | "dateRange">,
    graphName: BenchmarkForecastGraphName
  ) => Promise<BenchmarkForecastGraph>;
  getBenchmarkTopPortfolios: (
    queries: Pick<ReportsQueries, "filters" | "dateRange">,
    field: BenchmarkCalculatedField
  ) => Promise<BenchmarkDealWithResult[]>;
  getBenchmarkTable: (
    queries: Pick<
      ReportsQueries,
      "benchmarkTableColumns" | "filters" | "currency" | "exchangeMethod" | "groupBy" | "benchmarkIndexName"
    >
  ) => Promise<GetReportsDataResponse>;
  // getBenchmarkTelescopeTable: () => Promise<TelescopeTable>;
  getBenchmarkLiquidityGraph: (params: BenchmarkLiquidityGraphParams) => Promise<BenchmarkLiquidityGraph>;
}

export class BenchmarkQueries extends AsyncQueries implements IBenchmarkQueries {
  public constructor(params: AsyncQueriesParams) {
    super(params);
  }

  // public getBenchmarkTelescopeTable = async () => {
  //   const {
  //     data: { data: telescopeRes }
  //   } = await this.api.get<AltiusRes<{ tableData: TelescopeTable }>>({
  //     url: "/api/v0.0.3/benchmark/telescope"
  //   });
  //   return telescopeRes.tableData;
  // };

  public getBenchmarkFilters = async (filters: ReportFilters) => {
    const {
      data: { data: filtersRes }
    } = await this.api.post<AltiusRes<ReportFilters>>({
      url: "/api/v0.0.3/benchmark/filters",
      data: { filters }
    });

    // Sort vintage from latest to oldest - remove this code when server take this job
    if (filtersRes.hasOwnProperty("vintage")) {
      filtersRes.vintage = filtersRes.vintage.sort((a, b) => (b as number) - (a as number));
    }

    return filtersRes;
  };

  public getBenchmarkGraph = async (queries: BenchmarkQueriesType) => {
    const data = {
      ...queries,
      dateRange: convertDateRange(queries.dateRange)
    };

    const res = await this.api.post<AltiusRes<DealGraph>>({
      url: "/api/v0.0.3/reports/fetch-deal-graph",
      data: data
    });

    return res.data.data;
  };

  public getBenchmarkForecastGraph = async (
    queries: Pick<ReportsQueries, "filters" | "dateRange">
    // graphName: "capital_call" | "dpi" | "distribution" | "net_cashflow"
  ) => {
    // const graphNames = ["capital_call", "distribution", "net_cashflow"].join(",");
    const data = {
      ...queries,
      dateRange: convertDateRange(queries.dateRange)
    };

    const res = await this.api.post<AltiusRes<BenchmarkForecastGraph>>({
      url: "/api/v0.0.3/benchmark/graphs?graph_name=net_cashflow&graph_name=capital_call&graph_name=distribution",
      data: data
    });

    return res.data.data;
  };

  public getBenchmarkTopPortfolios = async (
    queries: Pick<ReportsQueries, "filters" | "dateRange" | "indexName">,
    field: BenchmarkCalculatedField
  ) => {
    const data = {
      ...queries,
      dateRange: convertDateRange(queries.dateRange)
    };

    const res = await this.api.post<AltiusRes<BenchmarkDealWithResult[]>>({
      url: "/api/v0.0.3/benchmark/top-performance?calcField=" + field,
      data: data
    });

    return res.data.data;
  };

  public getBenchmarkTable = async (
    queries: Pick<
      ReportsQueries,
      "benchmarkTableColumns" | "filters" | "currency" | "exchangeMethod" | "groupBy" | "benchmarkIndexName"
    >
  ) => {
    const { benchmarkTableColumns, exchangeMethod, benchmarkIndexName, ...tableQueries } = queries;

    const columns = benchmarkTableColumns.map((col) => ({
      ...col,
      ...(col.dateRange && {
        dateRange: { ...convertDateRange(col.dateRange as DateRangeValue), value: col.dateRange.value }
      })
    }));

    // const effectiveIndexName = indexName ? { index: indexName, name: indexNames[indexName] } : undefined;
    const effectiveIndexName = benchmarkIndexName;

    const hasPME = columns.some((col) => col.key?.startsWith("bm_pme"));

    const res = await this.api.post<AltiusRes<GetReportsDataResponse>>({
      url: "/api/v0.0.3/reports/fetch-measure-data",
      data: {
        pageType: ReportPageType.Benchmark,
        columns,
        exchangeDate: exchangeMethod,
        dateRange: { to: "today" },
        is_benchmark: true,
        ...(effectiveIndexName && hasPME && { indexName: effectiveIndexName }),
        ...tableQueries
      }
    });

    return res.data.data;
  };

  public getBenchmarkLiquidityGraph: IBenchmarkQueries["getBenchmarkLiquidityGraph"] = async (params) => {
    const res = await this.api.get<AltiusRes<BenchmarkLiquidityGraph>>({
      url: "/api/v0.0.3/benchmark/performance-graph",
      params
    });

    return res.data.data;
  };
}
